/**
 * Generates QR code data from attenders and patient information
 * @param {Array} attenders - Array of attender objects
 * @param {Object} selectedPatient - Patient information object
 * @returns {string} - JSON string of formatted data for QR code
 */
export const generateQRData = (attenders, selectedPatient) => {
  if (!Array.isArray(attenders)) {
    console.error('Expected attenders to be an array, but got:', attenders);
    return JSON.stringify({});
  }

  const attenderDetails = attenders.map((attender) => ({
    id: attender.id || Date.now(),
    // attender_name: attender.attender_name || attender.name || "",
    // phone: attender.phone || attender.phoneNumber || "",
  }));

  return JSON.stringify({
    patient_name: selectedPatient?.patientName,
    ward: selectedPatient.wardName,
    zone: selectedPatient?.Zone || '',
    wardId: selectedPatient?.wardId,
    admission_id: selectedPatient?.id,
    ward_type: selectedPatient?.wardType || '',
    attender_details: attenderDetails,
  });
};

/**
 * Handles printing attender passes
 * @param {Object} options - Options containing patient and attender information
 * @returns {Promise<boolean>} - Promise resolving to success status
 */
export const handlePrintAttenderPasses = async ({
  selectedPatient,
  attendersList,
  submitting,
  selectedAttenders,
}) => {
  try {
    // Determine which attenders to print
    let attendersToPrint = [];
    if (selectedAttenders && selectedAttenders.length > 0) {
      attendersToPrint = selectedPatient.attenders.filter((attender) =>
        selectedAttenders.includes(attender.id)
      );
    } else if (submitting && attendersList.length > 0) {
      attendersToPrint = attendersList;
    }

    if (!Array.isArray(attendersToPrint) || attendersToPrint.length === 0) {
      console.error('No attenders for printing.');
      return false;
    }

    // Generate QR data
    const qrData = generateQRData(attendersToPrint, selectedPatient);

    // Create a dedicated print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error(
        'Unable to open print window. Please check your popup blocker settings.'
      );
    }

    // Get today's date for the ID card
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-GB');

    // Determine display name for attenders based on count
    let attenderDisplay = '';
    if (attendersToPrint.length > 3) {
      const firstAttender =
        attendersToPrint[0].attender_name || attendersToPrint[0].name || '';
      attenderDisplay = `${firstAttender} (+${attendersToPrint.length - 1})`;
    } else if (attendersToPrint.length === 3) {
      const firstAttender =
        attendersToPrint[0].attender_name || attendersToPrint[0].name || '';
      attenderDisplay = `${firstAttender} (+2)`;
    } else {
      attenderDisplay = attendersToPrint
        .map((att) => att.attender_name || att.name || '')
        .join(', ');
    }

    // Get phone number
    const phoneNumber =
      attendersToPrint[0]?.phone || attendersToPrint[0]?.phoneNumber || 'N/A';

    // Write HTML to print window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>ID Card - ${selectedPatient.patientName}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          @page {
            size: 70mm 110mm;
            margin: 0;
            page-break-after: avoid;
            page-break-before: avoid;
          }
          body {
            margin: 0;
            padding: 4px;
            width: 70mm;
            height: 110mm;
            font-family: Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: white;
          }
          * {
            box-sizing: border-box;
          }
          .container {
            width: 100%;
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .date {
            position: absolute;
            top: 3mm;
            right: 2mm;
            font-size: 9px;
            font-weight: bold;
            color: black;
          }
          .header {
            margin-top: 12mm;
            margin-bottom: 7mm;
            text-align: center;
            width: 100%;
          }
          .header p {
            margin: 0;
            padding: 0;
            font-weight: bold;
            color: black;
          }
          .hospital-name {
            font-size: 12px;
            margin-bottom: 7px !important;
          }
          .card-type {
            font-size: 8px;
          }
          .qr-container {
            width: 40mm;
            height: 40mm;
            margin-bottom: 7mm;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .details {
            width: 100%;
            padding: 0 2mm;
          }
          .row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            width: 100%;
          }
          .column {
            width: 48%;
          }
          .label {
            font-size: 10px;
            font-weight: bold;
            color: black;
            display: block;
          }
          .value {
            font-size: 9px;
            font-weight: bold;
            color: black;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
          }
          .id-card {
            page-break-inside: avoid;
            page-break-after: always;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            #qrcode img {
              width: 200px !important;
              height: 180px !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="id-card">
          <div class="container">

            
            <div class="header">
              <p class="hospital-name">அரசு மோகன் குமாரமங்கலம் மருத்துவக் கல்லூரி மருத்துவமனை சேலம் -1</p>
              <p class="card-type">பார்வையாளர் அடையாள அட்டை( ${formattedDate} )</p>
            </div>
            
            <div class="qr-container">
              <div id="qrcode"></div>
            </div>
            
            <div class="details">
              <div class="row">
                <div class="column">
                  <span class="label">IP எண்:</span>
                  <span class="value">${selectedPatient.ipNumber}</span>
                </div>
                <div class="column">
                  <span class="label">பார்வையாளர்கள்:</span>
                  <span class="value">${attenderDisplay}</span>
                </div>
              </div>
              
              <div class="row">
                <div class="column">
                  <span class="label">நோயாளி பெயர்:</span>
                  <span class="value">${selectedPatient.patientName}</span>
                </div>
                <div class="column">
                  <span class="label">வார்டு:</span>
                  <span class="value">${selectedPatient.wardName}</span>
                </div>
              </div>
              
              <div class="row">
                <div class="column">
                  <span class="label">மொபைல்:</span>
                  <span class="value">${phoneNumber}</span>
                </div>
                    <div class="column">
                    <span class="label">மண்டலம்:</span>
                    <span class="value">${selectedPatient.Zone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
        <script>
          // Generate QR code with QRCode.js
          window.onload = function() {
            new QRCode(document.getElementById("qrcode"), {
              text: ${JSON.stringify(qrData)},
              width: 200,
              height: 180,
              colorDark: "#000000",
              colorLight: "#ffffff",
              correctLevel: QRCode.CorrectLevel.M
            });
            
            // Ensure QR code is displayed at correct size
            setTimeout(function() {
              const qrImg = document.querySelector("#qrcode img");
              if (qrImg) {
                qrImg.style.width = "200px";
                qrImg.style.height = "180px";
              }
              
              // Custom print function with options for continuous printing
              const printWithOptions = () => {
                try {
                  // Some thermal printer drivers accept custom options
                  if (typeof window.print === 'function') {
                    // Standard print dialog
                    window.print();
                  }
                } catch (err) {
                  console.error('Print error:', err);
                  // Fallback to standard print
                  window.print();
                }
              };
              
              // Wait for everything to render then print
              setTimeout(printWithOptions, 500);
            }, 500);
          };
        </script>
      </body>
      </html>
    `);

    printWindow.document.close();
    return true;
  } catch (error) {
    console.error('Error printing attender passes:', error);
    return false;
  }
};

// Handle batch printing for multiple attendant ID cards
export const handleBatchPrintAttenderPasses = async ({
  selectedPatient,
  attendersList,
  submitting,
  selectedAttenders,
}) => {
  try {
    // Determine which attenders to print
    let attendersToPrint = [];
    if (selectedAttenders && selectedAttenders.length > 0) {
      attendersToPrint = selectedPatient.attenders.filter((attender) =>
        selectedAttenders.includes(attender.id)
      );
    } else if (submitting && attendersList.length > 0) {
      attendersToPrint = attendersList;
    }

    if (!Array.isArray(attendersToPrint) || attendersToPrint.length === 0) {
      console.error('No attenders for printing.');
      return false;
    }

    // Create a dedicated print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error(
        'Unable to open print window. Please check your popup blocker settings.'
      );
    }

    // Get today's date for the ID card
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-GB');

    // Start HTML document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Batch ID Card Printing</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          @page {
            size: 70mm 110mm;
            margin: 0;
            page-break-after: always;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: white;
          }
          * {
            box-sizing: border-box;
          }
          .id-card {
            width: 70mm;
            height: 110mm;
            padding: 4px;
            page-break-after: always;
            page-break-inside: avoid;
          }
          .container {
            width: 100%;
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .date {
            position: absolute;
            top: 3mm;
            right: 2mm;
            font-size: 9px;
            font-weight: bold;
            color: black;
          }
          .header {
            margin-top: 12mm;
            margin-bottom: 7mm;
            text-align: center;
            width: 100%;
          }
          .header p {
            margin: 0;
            padding: 0;
            font-weight: bold;
            color: black;
          }
          .hospital-name {
            font-size: 12px;
            margin-bottom: 2px !important;
          }
          .card-type {
            font-size: 10px;
          }
          .qr-container {
            width: 40mm;
            height: 40mm;
            margin-bottom: 7mm;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .details {
            width: 100%;
            padding: 0 2mm;
          }
          .row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            width: 100%;
          }
          .column {
            width: 48%;
          }
          .label {
            font-size: 10px;
            font-weight: bold;
            color: black;
            display: block;
          }
          .value {
            font-size: 9px;
            font-weight: bold;
            color: black;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .qr-container img {
              width: 120px !important;
              height: 120px !important;
            }
          }
        </style>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
      </head>
      <body>
    `);

    // Add each attendant as a separate card with forced page break
    attendersToPrint.forEach((att, index) => {
      // Generate QR data for this specific attender
      const individualQrData = generateQRData([att], selectedPatient);

      printWindow.document.write(`
        <div class="id-card">
          <div class="container">
           
            
            <div class="header">
              <p class="hospital-name">அரசு மோகன் குமாரமங்கலம் மருத்துவக் கல்லூரி மருத்துவமனை சேலம் -1</p>
              <p class="card-type">பார்வையாளர் அடையாள அட்டை ( ${formattedDate} )</p>
            </div>
            
            <div class="qr-container">
              <div id="qrcode-${index}"></div>
            </div>
            
            <div class="details">
              <div class="row">
                <div class="column">
                  <span class="label">IP எண்:</span>
                  <span class="value">${selectedPatient.ipNumber}</span>
                </div>
                <div class="column">
                  <span class="label">பார்வையாளர்:</span>
                  <span class="value">${
                    att.attender_name || att.name || ''
                  }</span>
                </div>
              </div>
              
              <div class="row">
                <div class="column">
                  <span class="label">நோயாளி பெயர்:</span>
                  <span class="value">${selectedPatient.patientName}</span>
                </div>
                <div class="column">
                  <span class="label">வார்டு:</span>
                  <span class="value">${selectedPatient.wardName}</span>
                </div>
              </div>
              
              <div class="row">
                <div class="column">
                  <span class="label">மொபைல்:</span>
                  <span class="value">${
                    att.phone || att.phoneNumber || 'N/A'
                  }</span>
                </div>
                <div class="column">
                    <span class="label">மண்டலம்:</span>
                    <span class="value">${selectedPatient.Zone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `);
    });

    // Close body and add script to generate all QR codes
    printWindow.document.write(`
        <script>
          window.onload = function() {
            // Generate QR codes for each card
            ${attendersToPrint
              .map((att, index) => {
                const individualQrData = generateQRData([att], selectedPatient);

                return `
                new QRCode(document.getElementById("qrcode-${index}"), {
                  text: ${JSON.stringify(individualQrData)},
                  width: 120,
                  height: 120,
                  colorDark: "#000000",
                  colorLight: "#ffffff",
                  correctLevel: QRCode.CorrectLevel.M
                });
              `;
              })
              .join('\n')}
            
            // Wait for all QR codes to render
            setTimeout(function() {
              // Make sure all QR codes are properly sized
              document.querySelectorAll('[id^="qrcode-"] img').forEach(img => {
                img.style.width = "120px";
                img.style.height = "120px";
              });
              
              setTimeout(function() {
                window.print();
              }, 500);
            }, 1000);
          };
        </script>
      </body>
      </html>
    `);

    printWindow.document.close();
    return true;
  } catch (error) {
    console.error('Error printing batch attender passes:', error);
    return false;
  }
};
